"use client";
import { useState, useId } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";

import { triggerInfoBarOpened, handleStickyContent } from "@/src/store/stickyInfoBar";

import { createImageUrl } from "helpers/create-image-url";
import { favoriteHelpers } from "@/src/helpers/favorite";

import PlusIcon from "assets/svg/circle_outline_plus.svg";
import HeartIcon from "assets/svg/heart.svg";

import { addListingToFavoriteGroup, createNewFavoriteList } from "services/favorites";

import Input from "components/common/input";
import Button from "components/common/button";
import Loader from "components/loader";
import { LazyImage } from "components/common/lazy-image/LazyImage";

import styles from "./styles.module.css";

const ModalFavorite = ({ listingId, successCallback, failureCallback }) => {
  const keyId = useId();
  const dispatch = useDispatch();
  const getAllFavoritesByAccount = favoriteHelpers();

  const [formViewed, setFormViewed] = useState(false);
  const [listName, setListName] = useState("");
  const [isFormSending, setIsFormSending] = useState(false);
  const [createFormErrorText, setCreateFormErrorText] = useState(null);

  const favoriteGroupsReduxState = useSelector((state) => state.favorite.favoriteList);

  const handleFavoriteList = async (groupId, groupName) => {
    setIsFormSending(true);
    let response;
    try {
      response = await addListingToFavoriteGroup(groupId, listingId);
      if (response.status === "success") {
        successCallback && successCallback();
        dispatch(handleStickyContent(`İlan <strong>${groupName}</strong> favori listenize eklendi.`));
        dispatch(triggerInfoBarOpened());
      } else if (response.status === "error") {
        handleErrorStickyContent(response);
      }
    } catch (error) {
      console.error(`Add item to favorite list error!! ${error}`);
      handleErrorStickyContent(response);
    } finally {
      setIsFormSending(false);
    }
  };

  const handleErrorStickyContent = (responseParam) => {
    failureCallback && failureCallback();
    const message = responseParam?.description?.tr || "Bir hata oluştu.";
    dispatch(handleStickyContent(message));
    dispatch(triggerInfoBarOpened());
  };

  const handleNewFavoriteList = async () => {
    try {
      const response = await createNewFavoriteList(listName);
      if (response.status === "success") {
        getAllFavoritesByAccount(() => {
          setFormViewed(false);
        });
      } else if (response.status === "error") {
        setCreateFormErrorText(response.description?.tr);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.description?.tr || "Bir hata oluştu.";
      setCreateFormErrorText(errorMessage);
    }
  };

  const handleListName = (e) => {
    setListName(e.target.value);
    setCreateFormErrorText(null);
  };

  const loaderClasses = classNames({
    [styles.loaderWrap]: true,
    [styles.loaderWrapViewed]: isFormSending,
  });

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.title}>{formViewed ? "Favori Listesi Oluştur" : "Favori Listeme Ekle"}</h1>
      <div className={styles.inner}>
        <>
          <div className={loaderClasses}>
            <Loader />
          </div>
          {formViewed ? (
            <>
              <div className={styles.formWrap}>
                <div className={styles.imageWrap}></div>
                <div className={styles.textWrap}>
                  <Input placeholder="Liste Adı Giriniz" onChange={handleListName} />
                </div>
              </div>
              {createFormErrorText && <span className={styles.error}>{createFormErrorText}</span>}
              <div className={styles.formActions}>
                <Button disable={listName.length === 0} size="M" onClick={handleNewFavoriteList}>
                  Kaydet
                </Button>
                <span className={styles.textLink} onClick={() => setFormViewed(false)}>
                  Vazgeç
                </span>
              </div>
            </>
          ) : (
            <>
              {favoriteGroupsReduxState && favoriteGroupsReduxState.length ? (
                <ul className={styles.favoriteGroups}>
                  {favoriteGroupsReduxState.map((item, index) => (
                    <li key={`${keyId}-${index}`} onClick={() => handleFavoriteList(item.id, item.title)}>
                      <div className={styles.imageWrap}>
                        {item.coverPhotoUrl ? (
                          <LazyImage
                            src={createImageUrl(item.coverPhotoUrl, {
                              width: 48,
                              height: 48,
                            })}
                            alt={item.title}
                          />
                        ) : (
                          <HeartIcon />
                        )}
                      </div>
                      <div className={styles.listInfoWrap}>
                        <span className={styles.groupName}>{item.title}</span>
                        <span className={styles.groupNumber}>{item.count || 0} İlan</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.info}>
                  Favori listeniz bulunmamaktadır. İlanı favorilerinize eklemek için yeni liste oluşturabilirsiniz.
                </p>
              )}
              <div className={styles.action}>
                <span className={styles.actionIcon}>
                  <PlusIcon />
                </span>
                <span className={styles.actionText} onClick={() => setFormViewed(!formViewed)}>
                  Yeni Liste Oluştur
                </span>
              </div>
            </>
          )}
        </>
      </div>
    </div>
  );
};

ModalFavorite.propTypes = {
  listingId: PropTypes.number,
  successCallback: PropTypes.func,
  failureCallback: PropTypes.func,
};
export default ModalFavorite;
